import prependHttp from 'prepend-http';
import { CONSENT } from '../../enums/RoomConsent';
import { getConsent } from '../../lib/Consent';
import {
  getContentSourceName,
  RoomContentContextProps,
} from '../../lib/RoomContentContext';
import { Referrers } from '../../enums/Analytics';
import { ViewType } from '../../enums/ContentUrl';
import { ContentSchema } from '../../definitions/schemas';

enum SourceType {
  ROOM_EDIT = 'room_edit',
  ROOM_PREVIEW = 'room_preview',
  ROOM_STATISTICS = 'room_statistics',
  ROOM_URL = 'room_url',
  TEMPLATE_EDIT = 'room_template_edit',
  TEMPLATE_PREVIEW = 'room_template_preview',
}

export enum UrlParams {
  CONTACT = 'dsr_contact_id',
  DSR = 'dsr',
  ROOM = 'room_id',
  SOURCE = 'source',
  TEMPLATE = 'template_id',
  TYPE = 'sourceType',
  URL = 'interactionURL',
  USER = 'dsr_user_id',
  VIEW = 'viewType',
  OPPORTUNITY_ID = 'opportunity_id',
  CONTACT_CONSENT = 'contact_consent',
  IS_DOWNLOADABLE = 'isDownloadable',
  HIDE_MEDIA_HEADER = 'hideMediaHeader',
  ENABLE_VIEWPORT_TRACKING = 'enableViewportTracking',
  DSR_CONTENT_ID = 'dsrContentId',
}

const REFERRER_TO_SOURCE_TYPE = {
  [`${Referrers.ROOM_TEMPLATES.HOME}-${Referrers.ROOM_TEMPLATES.CREATE} - Preview`]:
    SourceType.TEMPLATE_PREVIEW,
  [`${Referrers.ROOM_TEMPLATES.HOME}-${Referrers.ROOM_TEMPLATES.EDIT} - Preview`]:
    SourceType.TEMPLATE_PREVIEW,
  [`${Referrers.DEAL_ROOMS.STATISTICS}-${Referrers.DEAL_ROOMS.EDIT} - Preview`]:
    SourceType.ROOM_PREVIEW,
  [`${Referrers.DEAL_ROOMS.HOME}-${Referrers.DEAL_ROOMS.CREATE} - Preview`]:
    SourceType.ROOM_PREVIEW,
  [`${Referrers.DEAL_ROOMS.HOME}-${Referrers.DEAL_ROOMS.EDIT} - Preview`]:
    SourceType.ROOM_PREVIEW,
  [`${Referrers.ROOM_TEMPLATES.HOME}-${Referrers.ROOM_TEMPLATES.CREATE}`]:
    SourceType.TEMPLATE_EDIT,
  [`${Referrers.ROOM_TEMPLATES.HOME}-${Referrers.ROOM_TEMPLATES.EDIT}`]:
    SourceType.TEMPLATE_EDIT,
  [`${Referrers.DEAL_ROOMS.STATISTICS}-${Referrers.DEAL_ROOMS.EDIT}`]:
    SourceType.ROOM_EDIT,
  [`${Referrers.DEAL_ROOMS.HOME}-${Referrers.DEAL_ROOMS.CREATE}`]:
    SourceType.ROOM_EDIT,
  [`${Referrers.DEAL_ROOMS.HOME}-${Referrers.DEAL_ROOMS.EDIT}`]:
    SourceType.ROOM_EDIT,
  [`${Referrers.DEAL_ROOMS.HOME}-${Referrers.DEAL_ROOMS.CLONE} - Preview`]:
    SourceType.ROOM_EDIT,
  [`${Referrers.DEAL_ROOMS.HOME}-${Referrers.DEAL_ROOMS.CLONE}`]:
    SourceType.ROOM_EDIT,
  [`${Referrers.ROOM_TEMPLATES.HOME} - Preview`]: SourceType.TEMPLATE_PREVIEW,
  [Referrers.DEAL_ROOMS.CONTACT_VIEW]: SourceType.ROOM_URL,
  [Referrers.DEAL_ROOMS.STATISTICS]: SourceType.ROOM_STATISTICS,
  [Referrers.DEAL_ROOMS.USER_VIEW]: SourceType.ROOM_URL,
  [Referrers.DEAL_ROOMS.UPDATE_EMAIL]: SourceType.ROOM_URL,
  [Referrers.DEAL_ROOMS.REMINDER_EMAIL]: SourceType.ROOM_URL,
  [Referrers.DEAL_ROOMS.SHARED_EMAIL]: SourceType.ROOM_URL,
};

/**
 * URL query string params that are added to a content's URL if it's a Mindtickle
 * content. This new prepared URL is used instead for the iframe
 *  - dsr: '1'
 *  - interactionURL: url + dsr=1 query string param
 *      *note: url was probably pre-prepared (comes from the interactions/replace endpoint)
 *  - sourceType: room_edit | room_preview | room_statistics | room_url | room_template_edit | room_template_preview
 *  - room_id: uuid (optional: if creating room)
 *  - template_id: template id (optional: if creating room template)
 *  - source: dn (deal name or template name) | `${dn},${rn}` (only for rooms if room name is present)
 *  - view: external (only if it's being viewed on the room's view page as a contact) | internal
 *  - dsr_contact_id: contact id (optional if viewing from the admin side)
 *  - dsr_user_id: currently logged user id (optional if viewing from the room's view page as a contact)
 */
interface VendorUrlParams {
  contextProps: RoomContentContextProps;
  url: string;
  referrer: string;
  isDownloadable?: boolean;
  track?: boolean;
  isInlineIframeViewer?: boolean;
  contentId: string;
  isConsentDeclinedSuppression?: boolean;
}

export const appendVendorUrlParams = ({
  contextProps,
  url,
  referrer,
  isDownloadable = false,
  track,
  isInlineIframeViewer = false,
  contentId,
  isConsentDeclinedSuppression = false,
}: VendorUrlParams) => {
  const { isContact, visitorId, opportunityId } = contextProps;

  // prependHttp is used to avoid errors
  const newUrl = new URL(prependHttp(url));
  const type = REFERRER_TO_SOURCE_TYPE[referrer];
  const view = track && isContact ? ViewType.EXTERNAL : ViewType.INTERNAL;

  // let variables that can change between templates and rooms
  // Rooms is the default case, if the source type is from templates
  // the values are updated
  const source = getContentSourceName(contextProps);
  let id = contextProps.roomUUID;
  let nameField = UrlParams.ROOM;

  if ([SourceType.TEMPLATE_EDIT, SourceType.TEMPLATE_PREVIEW].includes(type)) {
    id = contextProps.templateId;
    nameField = UrlParams.TEMPLATE;
  }

  // The vendor params are added following an order provided by MT
  newUrl.searchParams.set(UrlParams.DSR, '1');
  if (isInlineIframeViewer) {
    newUrl.searchParams.set(UrlParams.HIDE_MEDIA_HEADER, 'true');
    newUrl.searchParams.set(UrlParams.ENABLE_VIEWPORT_TRACKING, 'true');
  }
  newUrl.searchParams.append(UrlParams.URL, newUrl.toString());
  newUrl.searchParams.append(UrlParams.TYPE, type);
  if (id) {
    // The id field is optional if we are creating a room/template
    newUrl.searchParams.append(nameField, id);
  }
  // @see https://mindtickle.atlassian.net/browse/DSR-1558 for more details on special characters
  const sanitizedSource = source.replace(/['<>]/g, '');
  newUrl.searchParams.append(UrlParams.SOURCE, sanitizedSource);
  newUrl.searchParams.append(UrlParams.VIEW, view);
  newUrl.searchParams.append(UrlParams.DSR_CONTENT_ID, contentId);
  newUrl.searchParams.append(
    isContact ? UrlParams.CONTACT : UrlParams.USER,
    visitorId!
  );

  if (opportunityId) {
    newUrl.searchParams.append(UrlParams.OPPORTUNITY_ID, opportunityId);
  }

  /**
   * The consent is passed as a query string param for iframes belonging to
   * storage providers (e.g. Asset Hub), which according to that consent, should
   * allow tracking or not. These are the different scenarios and the respective value:
   *  1. Users (admin/salespeople) visiting one of their org's rooms (not through vanity URL)
   *      => 0 (rejected, they must not be tracked and are never prompted for consent)
   *  2. Contacts OR Users (admin/salespeople) visiting a room through vanity URL
   *    2.1. Consent for EU only
   *      2.1.1. Visits from EU
   *        => the value they provide for consent (0 or 1, available from the Cookie)
   *      2.1.2. Visits from non-EU
   *        => 1 (they are not prompted for consent, but they're going to be tracked anyway)
   *    2.2. Consent for all regions
   *      => the value they provide for consent (0 or 1, available from the Cookie)
   */
  const persistedConsent = getConsent()?.consent;
  let consent = CONSENT.REJECTED;

  if (
    isContact &&
    !(isConsentDeclinedSuppression && persistedConsent === CONSENT.REJECTED)
  ) {
    consent = CONSENT.ACCEPTED;
  }

  newUrl.searchParams.append(UrlParams.CONTACT_CONSENT, consent);

  newUrl.searchParams.append(UrlParams.IS_DOWNLOADABLE, String(isDownloadable));

  return newUrl.toString();
};

/**
 * Frame height for Asset Hub content opened in theater mode: the theater
 * chrome (app bar, paddings, title rows) takes ~220px — a taller frame
 * pushes the embedded viewer's bottom zoom toolbar out of view and grows
 * a second scrollbar (PCUTS-260)
 */
export const THEATER_FRAME_HEIGHT = 'calc(100vh - 220px)';

export const isExternalUrl = (url: string, content: Partial<ContentSchema>) => {
  if (
    (content.mtData && !!Object.keys(content.mtData).length) ||
    !content?.url?.openGraph
  ) {
    return false;
  }

  return true;
};
